import React, { Component } from 'react';
import get from 'lodash/get';
import moment from 'moment-timezone';
import { Typography, Button } from '@mui/material';
import Link from '@mui/material/Link';

import {
  TaskTypes,
  TaskPriorityNames,
  getDeploymentStatusReadable,
  getPossessionStatusReadable,
  getExtStatusReadable,
} from '../../../lib/utils';

import Marker from './marker';

const keyValueDisplay = (key, value) => (
  <div style={{ marginTop: '5px' }}>
    <Typography>
      {key}
      :
      {value}
    </Typography>
  </div>
);

const iconStyle = (
  color = 'blue',
  opacity = 0.9,
  strokeWidth = 3,
  size = null,
  inspect = false,
  vehicleIndex,
  isRun = false,
) => {
  const svgString = `
    <svg width="${size}" height="${size + 7}" viewBox="0 0 26 33" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
        <g transform="translate(0.000000, 1.000000)">
          <circle stroke="#000000" stroke-width="${strokeWidth}" opacity="${opacity}" fill="${color}" fill-rule="nonzero" cx="13" cy="8" r="8"></circle>
          ${inspect ? '<text x="13" y="13" text-anchor="middle" fill="black" font-family="Roboto sans-serif" font-size="14" font-weight="bold">i</text>' : ''}
          ${isRun ? `<g transform="translate(3.500000, 17.000000)">
            <rect stroke="#000000" fill="#FFFFFF" x="0.5" y="0.5" width="18" height="12" rx="2"></rect>
            <text x="38%" y="10" font-family="ArialMT" text-anchor="middle" font-size="9" font-weight="400" letter-spacing="0.13" fill="#000000">
              #${vehicleIndex + 1}
            </text>
          </g>` : ''}
        </g>
      </g>
    </svg>`;
  return `data:image/svg+xml;base64,${window.btoa(svgString)}`;
};

export const hasInspectTasks = (tasks) => tasks.some(
  (t) => t.task === TaskTypes.INSPECT && t.inspectReason,
);

export const VehicleMarkerType = {
  URGENT_PICKUP: 2,
  REGULAR_PICKUP: 1,
  REGULAR: 0,
};

const clusterColors = {
  [VehicleMarkerType.REGULAR]: '#5BC2E7',
  [VehicleMarkerType.REGULAR_PICKUP]: '#E5FF00',
  [VehicleMarkerType.URGENT_PICKUP]: '#E2231A',
};

export const getMarkerType = (vehicle) => {
  let markerType;

  if (vehicle.tasks && vehicle.tasks.length) {
    if (
      vehicle.tasks.some(
        (t) => t.priority === TaskPriorityNames.URGENT
          && t.task === TaskTypes.PICK_UP,
      )
    ) {
      markerType = VehicleMarkerType.URGENT_PICKUP;
    } else if (
      vehicle.tasks.some(
        (t) => t.priority === TaskPriorityNames.REGULAR
          && t.task === TaskTypes.PICK_UP,
      )
    ) {
      markerType = VehicleMarkerType.REGULAR_PICKUP;
    } else if (
      vehicle.tasks.some(
        (t) => t.priority === TaskPriorityNames.REGULAR
          && t.task === TaskTypes.INSPECT,
      )
    ) {
      markerType = VehicleMarkerType.REGULAR;
    }
  } else markerType = VehicleMarkerType.REGULAR;

  return markerType;
};

const getMarkerStyle = (vehicle, vehicleIndex, isRun) => {
  const markerType = getMarkerType(vehicle);
  const icon = iconStyle(
    clusterColors[markerType],
    1,
    2,
    26,
    markerType === VehicleMarkerType.REGULAR && hasInspectTasks(vehicle.tasks),
    vehicleIndex,
    isRun,
  );

  return {
    url: icon,
    scaledSize: { width: 26, height: 33 },
    anchor: { x: 13, y: 11 },
  };
};

class VehicleMarker extends Component {
  get coordinates() {
    const { vehicle } = this.props;
    return get(vehicle, 'geo.geoJson.coordinates', null);
  }

  get markerProps() {
    const {
      vehicle, zIndex, vehicleIndex, isRun = false,
    } = this.props;
    const {
      humanId, extStatus, extDeploymentStatus, extPossessionStatus,
    } = vehicle;
    const { coordinates } = this;
    const title = (
      <div>
        <br />
        <Typography variant="h6">{humanId}</Typography>
        <Typography variant="body2">
          {getExtStatusReadable(extStatus)}
          {' '}
          /
          {' '}
          {getDeploymentStatusReadable(extDeploymentStatus)}
          {' '}
          /
          {' '}
          {getPossessionStatusReadable(extPossessionStatus)}
        </Typography>
        <br />
      </div>
    );
    return {
      markerStyle: getMarkerStyle(vehicle, vehicleIndex, isRun),
      key: vehicle._id,
      position: { lat: coordinates[1], lng: coordinates[0] },
      infoTitle: title,
      infoItemId: `vehicle-${humanId}`,
      zIndex,
    };
  }

  get lastGeoUpdate() {
    const { vehicle } = this.props;
    const tz = get(vehicle.market, 'tz', 'America/New_York');
    return vehicle.geoUpdatedAt
      ? moment.tz(vehicle.geoUpdatedAt, tz).format('llll')
      : 'Unknown';
  }

  get lastGeoCoords() {
    const { vehicle } = this.props;
    const coordinates = get(vehicle, 'geo.geoJson.coordinates');
    if (coordinates) {
      const lat = coordinates[1].toFixed(2);
      const lng = coordinates[0].toFixed(2);
      return `[${lat}, ${lng}]`;
    } else {
      return 'Unknown';
    }
  }

  renderExtraInfo() {
    const {
      vehicle, fetchOneTask, setOneTask, readOnly = false, openVehicleForm,
    } = this.props;

    const {
      _id: vehicleId,
      marketId,
      needsMaintenance,
      tasks = [],
    } = vehicle;
    const inspectTasks = tasks.filter(
      (task) => task.task === TaskTypes.INSPECT && task.inspectReason,
    );
    const pickupTasks = tasks.filter(
      (task) => task.task === TaskTypes.PICK_UP && task.pickupReason,
    );
    const taskData = {
      marketId,
      assignedVehicleIds: [vehicleId],
      task: TaskTypes.PICK_UP,
    };

    return (
      <div style={{ width: '300px' }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => openVehicleForm(vehicle, true)}
        >
          History
        </Link>
        &nbsp;|&nbsp;
        <Link
          component="button"
          variant="body2"
          onClick={() => openVehicleForm(vehicle)}
        >
          Detail
        </Link>
        {keyValueDisplay('Battery Level', `${vehicle.batteryLevel || 0}%`)}
        {keyValueDisplay('Last Comm', this.lastGeoUpdate)}
        {keyValueDisplay('Location', this.lastGeoCoords)}
        {keyValueDisplay('Maintenance', needsMaintenance ? 'True' : 'False')}
        {keyValueDisplay(
          'Inspect Reasons',
          inspectTasks.length > 0
            ? inspectTasks.map((t) => t.inspectReason).join(', ')
            : 'None',
        )}
        {keyValueDisplay(
          'Pickups',
          pickupTasks.length > 0
            ? pickupTasks.map((task, i) => {
              const addComma = i < tasks.length - 1;
              return (
                <Link
                  key={task._id}
                  component="button"
                  style={{ marginLeft: '5px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fetchOneTask(task._id);
                  }}
                >
                  {`${task.pickupReason}${addComma ? ',' : ''}`}
                </Link>
              );
            })
            : 'None',
        )}
        {!readOnly && (
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={() => setOneTask(taskData)}
            style={{ marginTop: '5px' }}
          >
            Request Pickup
          </Button>
        )}
      </div>
    );
  }

  render() {
    const {
      vehicle, onClick, onClose, isSelected, clusterer,
    } = this.props;

    if (this.coordinates) {
      return (
        <Marker
          key={vehicle._id}
          marker={this.markerProps}
          renderExtraInfo={() => this.renderExtraInfo()}
          onClick={() => onClick(vehicle)}
          onClose={onClose}
          isSelected={isSelected}
          clusterer={clusterer}
        />
      );
    } else {
      return null;
    }
  }
}

export default VehicleMarker;
